"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useLanguage } from "@/contexts/language-context"
import { demoOptions, type DemoOption } from "@/lib/demo-data"
import { Sparkles } from "lucide-react"

interface DemoSelectorProps {
  onRunDemo: (option: DemoOption) => void
  isLoading?: boolean
}

export function DemoSelector({ onRunDemo, isLoading = false }: DemoSelectorProps) {
  const [selectedOption, setSelectedOption] = useState<string>("")
  const { t } = useLanguage()

  const handleRunDemo = () => {
    const option = demoOptions.find(opt => opt.id === selectedOption)
    if (option) {
      onRunDemo(option)
    }
  }

  return (
    <Card className="glass dark:glass-dark shadow-xl border-0 bg-white/95 dark:bg-zinc-900/95">
      <CardContent className="p-8">
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t("demo.title")}
          </h3>

          <div className="space-y-3">
            {demoOptions.map((option, index) => (
              <label
                key={option.id}
                className={`flex items-start gap-3 p-3 rounded-lg border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-800/50 cursor-pointer transition-colors ${selectedOption === option.id ? 'demo-option-selected' : ''
                  }`}
              >
                <input
                  type="radio"
                  name="demo-option"
                  value={option.id}
                  checked={selectedOption === option.id}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="demo-radio"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {index + 1}、{t(`demo.option${index + 1}`)}
                  </span>
                </div>
              </label>
            ))}
          </div>

          <Button
            onClick={handleRunDemo}
            disabled={!selectedOption || isLoading}
            className="w-full professional-button bg-blue-600 hover:bg-blue-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 text-white font-semibold py-4 text-base shadow-lg rounded-xl"
            size="lg"
          >
            <Sparkles className="mr-2 h-5 w-5" />
            {t("demo.runDemo")}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}